import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Shield, Bell, BarChart3, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const ForHospitals: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const benefits = [
    {
      icon: Clock,
      title: "Reduce OR delays due to backorders",
      description: "Get instant access to clinically approved alternatives when your preferred products are unavailable."
    },
    {
      icon: Shield,
      title: "Avoid clinician pushback with vetted substitutes",
      description: "Every recommendation is backed by clinical expertise and compliance standards."
    },
    {
      icon: Bell,
      title: "Receive alerts from distributors on a unified platform",
      description: "Centralized notifications keep your team informed about availability and alternatives."
    },
    {
      icon: BarChart3,
      title: "Data Analytics to prepare and address Backorders",
      description: "Predictive insights help you proactively manage supply chain disruptions."
    }
  ];

  const testimonials = [
    {
      quote: "When do I get this and how fast?",
      author: "Supply Chain Director",
      title: "Supply Chain Director",
      organization: "Saint Peter's Healthcare System"
    },
    {
      quote: "Dealing with backorders and sourcing substitutes is a huge pain point in virtually all of our sites — your product addresses that.",
      author: "Supply Chain Manager",
      title: "Supply Chain Manager",
      organization: "Kaiser Permanente"
    },
    {
      quote: "Your product is onto addressing a real slow point in our day to day workflows",
      author: "Clinical Value Analysis Nurse",
      title: "Clinical Value Analysis Nurse",
      organization: "RWJ Health System"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <section id="for-hospitals" className="py-16 sm:py-20 lg:py-32 bg-gradient-to-br from-primary-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 lg:mb-24"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6 px-2">
            For Hospitals
          </h2>
          <p className="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto px-4">
            Streamline your supply chain operations and ensure uninterrupted patient care with AI-powered clinical alternatives.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16 lg:mb-24">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="card p-5 sm:p-6 text-center"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <benefit.icon size={24} className="sm:w-8 sm:h-8 text-primary-600" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-2 sm:mb-3">
                {benefit.title}
              </h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div className="card p-6 sm:p-8 lg:p-12">
            <Quote size={32} className="sm:w-12 sm:h-12 text-primary-400 mx-auto mb-4 sm:mb-6" />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <blockquote className="text-lg sm:text-xl lg:text-2xl text-neutral-900 font-medium leading-relaxed mb-4 sm:mb-6 px-2">
                  "{testimonials[currentTestimonial]?.quote || ''}"
                </blockquote>
                
                <div className="text-neutral-600">
                  <p className="font-semibold text-base sm:text-lg">{testimonials[currentTestimonial]?.author || ''}</p>
                  <p className="text-sm sm:text-base">{testimonials[currentTestimonial]?.organization || ''}</p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Dots */}
            <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'bg-primary-400' : 'bg-neutral-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <div className="flex justify-between items-center mt-6 sm:mt-8">
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="p-2 sm:p-3 rounded-full bg-neutral-100 hover:bg-primary-100 transition-colors touch-manipulation"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} className="sm:w-6 sm:h-6 text-neutral-600" />
              </button>
              
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="p-2 sm:p-3 rounded-full bg-neutral-100 hover:bg-primary-100 transition-colors touch-manipulation"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} className="sm:w-6 sm:h-6 text-neutral-600" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export { ForHospitals }; 